<template>
  <div>
    <UlTabs
      v-if="!isUpdate"
      v-model="examType"
      :items="examinationTypes.map(t => t.text)"
      tabs-only
      class="mb-4"
    />
    <v-tabs-items v-model="examType">
      <v-tab-item v-for="(type, typeIndex) in examinationTypes" :key="typeIndex">
        <MedicalProcedureFormContent
          :key="type.value"
          :form-type="type.value"
          :medical-procedure-data="medicalProcedureData"
          :is-update="isUpdate"
        />
      </v-tab-item>
    </v-tabs-items>
  </div>
</template>

<script>
import { gql } from 'graphql-request';
import { mapActions, mapGetters, mapMutations } from 'vuex';
import lodash from 'lodash';
import preventionData from '~/mixins/preventionData';
import checkUserVerification from '~/mixins/checkUserVerification';
import preSignedUpload from '~/mixins/preSignedUpload';
import partnerInfo from '~/mixins/partnerInfo.js';
import medicalProcedures from '~/mixins/medicalProcedures.js';

export default {
  name: 'MedicalProcedureForm',
  mixins: [preventionData, checkUserVerification, preSignedUpload, partnerInfo, medicalProcedures],
  props: {
    medicalProcedureData: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      examType: 0,
      isValidDateTime: null
    };
  },
  computed: {
    ...mapGetters({
      date: 'forms/date/date',
      isValidDate: 'forms/date/isValid',

      time: 'forms/time/time',
      isValidTime: 'forms/time/isValid',

      attachments: 'forms/attachments/attachments',
      editEntryFormData: 'profile/prevention/editEntryFormData',

      entryNotes: 'profile/prevention/entryNotes'
    }),
    isUpdate() {
      return !lodash.isEmpty(this.editEntryFormData);
    },
    examinationTypes() {
      return [
        {
          text: this.$t(`medicalProcedure.${this.medicalProcedureData.type}.past`),
          value: 'report'
        },
        {
          text: this.$t(`medicalProcedure.${this.medicalProcedureData.type}.future`),
          value: 'appointment'
        }
      ];
    }
  },
  mounted() {
    if (this.isUpdate) {
      this.examType =
        this.appointments.findIndex(appointment => appointment.id === this.editEntryFormData.id) !==
        -1
          ? 1
          : 0;
    }
    this.setDialogData();
  },
  methods: {
    ...mapMutations({
      resetAttachments: 'forms/attachments/resetAttachments'
    }),
    ...mapActions({
      setDialogButtons: 'profile/medicalProcedure/setDialogButtons'
    }),
    onDateBlur() {
      document.querySelector('#notes').focus();
    },
    sendGtm(field) {
      this.gtmPush({
        event: 'form_progress',
        'form.field': field,
        'form.name': this.isUpdate ? 'editace report' : 'zapsani report',
        'medicalProcedure.type': this.medicalProcedureData.type,
        'medicalProcedure.name': this.medicalProcedureData.title
      });
    },
    validateForm() {
      this.$eventHub.$emit(
        `validateMedicalProcedureDateTime_${this.examinationTypes[this.examType].value}`,
        isValid => {
          this.isValidDateTime = isValid;
          this.$nextTick(() => {
            if (!this.isValidDate || !this.isValidDateTime) {
              this.scrollToError();
              return;
            }

            this.sendForm();
          });
        }
      );
    },
    async sendForm() {
      this.loading = true;

      if (!(await this.checkUserVerification(false, true))) {
        this.loading = false;
        return false;
      }

      let graphQlAttachments = [];

      if (this.attachments.length) {
        let mpAttachments = this.attachments
          .filter(att => !!att.id)
          .map(att => ({ awsId: att.id, name: att.name }));
        const attachmentsToUpload = this.attachments.filter(att => !!att.uuid);
        if (attachmentsToUpload?.length) {
          const uploadedAttachments = await this.processFiles(attachmentsToUpload, error => {
            this.handlePreSignedError(error, this.examinationTypes[this.examType].value);
          });

          if (!uploadedAttachments) {
            this.loading = false;
            return;
          }

          mpAttachments.push(...uploadedAttachments);
        }

        graphQlAttachments = mpAttachments.map(attachment => {
          return {
            id: attachment.awsId,
            name: attachment.name
          };
        });
      }

      const examTypeForMutations = this.examType === 1 ? 'Appointment' : 'Report';
      const mutationName = `${this.isUpdate ? 'update' : 'create'}${this.capitalizeFirstLetter(
        this.medicalProcedureData.type
      )}${examTypeForMutations}`;

      const dateParamName = this.examType === 1 ? 'dateTime' : 'date';
      const primaryKeyParamName = `${this.medicalProcedureData.type}${
        this.isUpdate ? examTypeForMutations : 'Info'
      }Id`;

      const query = gql`
        mutation ${mutationName}(
          $attachments: [AttachmentInput!],
          $${dateParamName}: String!,
          $${primaryKeyParamName}: ID!,
          $profileId: ID!,
          $text: String
        ) {
          ${mutationName}(
            attachments: $attachments,
            ${dateParamName}: $${dateParamName},
            ${primaryKeyParamName}: $${primaryKeyParamName},
            profileId: $profileId,
            text: $text
          )
        }
      `;

      const variables = {
        attachments: graphQlAttachments,
        profileId: this.$auth.user.profileId,
        text: this.entryNotes,
        [dateParamName]:
          dateParamName === 'dateTime'
            ? new Date(`${this.date}T${this.time}:00`).toISOString()
            : this.date,
        [primaryKeyParamName]: this.isUpdate
          ? this.editEntryFormData.id
          : this.medicalProcedureData.id
      };

      try {
        await this.$graphql.medicalProcedures.request(query, variables).then(async () => {
          this.gtmPush({
            event: 'form_sent_success',
            'form.name': this.isUpdate ? 'editace report' : 'zapsani report',
            'medicalProcedure.type': this.medicalProcedureData.type,
            'medicalProcedure.name': this.medicalProcedureData.title
          });

          if (!this.isUpdate) {
            this.sendAnalyticsUl({
              activity: 'prevention_procedure_saved',
              partner_id: this.partnerId,
              partner_name: this.partnerName,
              activity_name: this.medicalProcedureData.title,
              activity_id: this.medicalProcedureData.id
            });
          }

          if (examTypeForMutations === 'Appointment') {
            await this.sendMedicalProcedureToClinics(
              this.$auth.user.profileId,
              this.medicalProcedureData.id,
              this.date
            );
          }

          await this.closeModal();
        });
      } catch (err) {
        this.handleError({ error: err, code: '0x00000016' });
        this.loading = false;
      }
    },
    async closeModal() {
      this.$emit('close');
    },
    setDialogData() {
      this.setDialogButtons([
        {
          props: {
            color: 'tetrary',
            dataCy: 'medicalProcedureFormCloseButton'
          },
          events: {
            click: () => {
              this.$emit('close');
            }
          },
          text: this.$t('close'),
          orderMobile: 1
        },
        {
          type: 'sendButton',
          props: {
            loading: this.loading,
            'button-text-loading': this.$t('saveChangesProcess'),
            'button-text-success': this.$t('saveChangesSuccess'),
            dataCy: 'medicalProcedureFormSubmitButton'
          },
          events: {
            click: () => {
              this.validateForm();
            }
          },
          text: this.$t('save'),
          orderMobile: 0
        }
      ]);
    }
  },
  watch: {
    loading() {
      this.setDialogData();
    }
  }
};
</script>
